import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface HoroscopeProgressStage {
  from: number
  label: string
}

export interface HoroscopeProgressBarProps {
  active: boolean
  className?: string
  durationMs?: number
  etaLabel?: string
  stages?: HoroscopeProgressStage[]
}

const DEFAULT_STAGES: HoroscopeProgressStage[] = [
  { from: 0, label: 'Calculating planetary positions using NASA data' },
  { from: 0.18, label: 'Casting your rasi and navamsa charts' },
  { from: 0.4, label: 'Mapping nakshatras and dashas' },
  { from: 0.65, label: 'Reading yogas and drishti aspects' },
  { from: 0.88, label: 'Finalising your horoscope' },
]

const CEILING = 0.99

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

export function HoroscopeProgressBar({
  active,
  className,
  durationMs = 35000,
  etaLabel,
  stages = DEFAULT_STAGES,
}: HoroscopeProgressBarProps) {
  const [progress, setProgress] = useState(0)
  const startRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    if (!active) {
      startRef.current = null
      setProgress(0)
      return
    }

    startRef.current = null
    setProgress(0)

    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now
      const elapsed = now - startRef.current
      const linear = Math.min(elapsed / durationMs, 1)
      const eased = easeInOutCubic(linear)

      setProgress(Math.min(eased, CEILING))

      if (linear < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [active, durationMs])

  const pct = Math.min(99, Math.round(progress * 100))
  const stage =
    [...stages].reverse().find((item) => progress >= item.from) ?? DEFAULT_STAGES[0]

  return (
    <div
      className={cn(
        'w-full rounded-lg border border-violet-100 bg-gradient-to-br from-white via-violet-50/50 to-fuchsia-50/40 px-3 py-3 shadow-sm',
        className,
      )}
    >
      {etaLabel && (
        <p className="mb-2 text-center text-xs font-medium text-slate-500">
          {etaLabel}
        </p>
      )}
      <div
        className="relative h-2.5 w-full overflow-hidden rounded-full bg-slate-100 ring-1 ring-inset ring-slate-200/70"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={99}
        aria-valuenow={pct}
        aria-label={stage.label}
      >
        <div
          className="pointer-events-none absolute inset-y-[-4px] left-0 rounded-full bg-gradient-to-r from-indigo-500/30 via-violet-500/30 to-fuchsia-400/30 blur-md transition-[width] duration-150 ease-out"
          style={{ width: `${pct}%` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-y-0 left-0 overflow-hidden rounded-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 transition-[width] duration-150 ease-out"
          style={{ width: `${pct}%` }}
        >
          <div className="sarvalok-progress-shimmer absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/55 to-transparent" />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 text-[13px]">
        <div className="flex min-w-0 items-center gap-2 text-slate-600">
          <span
            className="sarvalok-progress-pulse h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500"
            aria-hidden="true"
          />
          <span className="min-w-0 truncate font-medium" aria-live="polite">
            {stage.label}
            <span className="sarvalok-progress-ellipsis" aria-hidden="true" />
          </span>
        </div>
        <span
          className="shrink-0 font-mono text-[11px] tabular-nums tracking-tight text-slate-500"
          aria-hidden="true"
        >
          {pct.toString().padStart(2, '0')}%
        </span>
      </div>

      <style>{`
        @keyframes sarvalokProgressShimmer {
          0% { transform: translateX(-30%); }
          100% { transform: translateX(420%); }
        }
        .sarvalok-progress-shimmer {
          animation: sarvalokProgressShimmer 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          will-change: transform;
        }
        @keyframes sarvalokProgressPulse {
          0%, 100% { opacity: 0.55; transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.35); }
          50% { opacity: 1; transform: scale(1.35); box-shadow: 0 0 0 4px rgba(139, 92, 246, 0); }
        }
        .sarvalok-progress-pulse {
          animation: sarvalokProgressPulse 1.4s ease-in-out infinite;
        }
        @keyframes sarvalokProgressEllipsis {
          0%, 100% { content: ""; }
          25% { content: "."; }
          50% { content: ".."; }
          75% { content: "..."; }
        }
        .sarvalok-progress-ellipsis::after {
          display: inline-block;
          width: 1.1em;
          text-align: left;
          content: "";
          animation: sarvalokProgressEllipsis 1.6s steps(1) infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .sarvalok-progress-shimmer,
          .sarvalok-progress-pulse,
          .sarvalok-progress-ellipsis::after {
            animation: none;
          }
        }
      `}</style>
    </div>
  )
}

export default HoroscopeProgressBar
